import React, { useCallback, useEffect, useRef, useState } from "react";
import { MediaBlockKind, useSetMcqQuestionBlockAnswerMutation } from "@axio/graphql/generated/graphql";
import VisualizationModal from "../../../components/chatNewInterface/interfaceComponents/visualizationModal/VisualizationModal";
import type {
  CanvasBlock,
  MessageVisualizationBlock,
} from "../../../components/chatNewInterface/interfaceComponents/visualizationModal/VisualizationModal";
import type { McqChatBlock, MessageItem } from "../aiTutorMessage.types";
import { appendInlineResponseFromMetadata } from "../../../utils/chat/inlineResponseBlocks";
import useLearningLlmStreamV2 from "../../../hooks/useLearningLlmStreamV2";

export type { CanvasBlock, MessageVisualizationBlock };

const asNullableString = (v: unknown): string | null => (typeof v === "string" ? v : null);
const asStringArray = (v: unknown): string[] =>
  Array.isArray(v) && v.every((c): c is string => typeof c === "string") ? v : [];

interface VisualizationModalContainerProps {
  visible: boolean;
  block: CanvasBlock;
  mediaChatId: string | null;
  isMediaChatReady?: boolean;
  onMediaChatId: (chatId: string) => void;
  contentNodeId: string;
  messages: MessageItem[];
  onMessagesChange: (updater: (prev: MessageItem[]) => MessageItem[]) => void;
  onClose: () => void;
  onError?: (title: string, message: string) => void;
}

const VisualizationModalContainer = ({
  visible,
  block,
  mediaChatId,
  isMediaChatReady = true,
  onMediaChatId,
  contentNodeId,
  messages,
  onMessagesChange,
  onClose,
  onError,
}: VisualizationModalContainerProps) => {
  const { startStream, isStreaming } = useLearningLlmStreamV2();
  const [setMcqQuestionBlockAnswerMutation] = useSetMcqQuestionBlockAnswerMutation();
  const [inputText, setInputText] = useState("");
  const activeChatIdRef = useRef<string | null>(mediaChatId);
  const streamingIdRef = useRef<string | null>(null);

  useEffect(() => {
    activeChatIdRef.current = mediaChatId;
  }, [mediaChatId]);

  const sendModalMessage = useCallback(
    async (text: string, options?: { inlineResponseChoiceIndex?: number }) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming) return;

      const chatId = activeChatIdRef.current;
      if (!chatId) {
        onError?.(
          "Media chat unavailable",
          isMediaChatReady
            ? "This media block does not have a chat yet."
            : "Still preparing chat for this media block. Please try again in a moment."
        );
        return;
      }

      const userMsgId = `viz-user-${Date.now()}`;
      const aiMsgId = `viz-ai-${Date.now()}`;
      streamingIdRef.current = aiMsgId;

      const userMsg: MessageItem = { id: userMsgId, sender: "human", content: trimmed };
      const aiMsg: MessageItem = { id: aiMsgId, sender: "ai", content: "" };

      onMessagesChange((prev) => [...prev, userMsg, aiMsg]);

      try {
        await startStream(trimmed, {
          chatId,
          contentNodeId,
          inlineResponseChoiceIndex: options?.inlineResponseChoiceIndex,
          mediaFocus:
            block.kind === "video"
              ? (block.title ?? "video")
              : (block.visualizationDescription ?? block.visualizationType),
          mediaBlockKind: block.kind === "video" ? MediaBlockKind.Video : MediaBlockKind.Visualization,
          skipChatLookup: true,
          onChatId: (nextChatId) => {
            activeChatIdRef.current = nextChatId;
            onMediaChatId(nextChatId);
          },
          onChunk: ({ text: chunk }) => {
            const id = streamingIdRef.current;
            if (!id) return;
            onMessagesChange((prev) =>
              prev.map((m) => (m.id === id ? { ...m, content: `${m.content ?? ""}${chunk}` } : m))
            );
          },
          onToolResult: ({ tool, result }) => {
            const id = streamingIdRef.current;
            if (!id || tool !== "mcq") return;

            const questionId = asNullableString(result.questionId);
            const question = asNullableString(result.question);
            const choices = asStringArray(result.choices);
            if (!questionId || !question || choices.length < 2) return;

            const mcqBlock: McqChatBlock = {
              kind: "mcq",
              questionId,
              question,
              choices,
              selectedChoiceIndex: null,
              submitted: false,
            };

            onMessagesChange((prev) =>
              prev.map((m) => (m.id === id ? { ...m, content: "", blocks: [...(m.blocks ?? []), mcqBlock] } : m))
            );
          },
          onComplete: ({ event }) => {
            const id = streamingIdRef.current;
            if (event?.chatId) activeChatIdRef.current = event.chatId;

            if (id) {
              onMessagesChange((prev) =>
                prev.map((m) => {
                  if (m.id !== id) return m;
                  let updated = appendInlineResponseFromMetadata(m, event?.metadata);
                  if (event?.messageId && event.messageId !== id) {
                    updated = { ...updated, id: event.messageId };
                  }
                  return updated;
                })
              );
            }

            streamingIdRef.current = null;
          },
          onError: () => {
            streamingIdRef.current = null;
          },
        });
      } catch {
        streamingIdRef.current = null;
        onMessagesChange((prev) => prev.filter((m) => m.id !== userMsgId && m.id !== aiMsgId));
        onError?.("Media chat error", "Something went wrong while sending your message.");
      }
    },
    [isStreaming, contentNodeId, block, startStream, onMessagesChange, onMediaChatId, onError, isMediaChatReady]
  );

  const handleSend = useCallback(async () => {
    const text = inputText.trim();
    if (!text || isStreaming) return;
    setInputText("");
    await sendModalMessage(text);
  }, [inputText, isStreaming, sendModalMessage]);

  const handleChoiceSelect = useCallback(
    (messageId: string, blockIndex: number, choiceIndex: number) => {
      if (isStreaming) return;

      const msg = messages.find((m) => m.id === messageId);
      const choiceBlock = msg?.blocks?.[blockIndex];
      if (
        !choiceBlock ||
        (choiceBlock.kind !== "mcq" && choiceBlock.kind !== "inline_response") ||
        choiceBlock.submitted
      ) {
        return;
      }

      const choiceText = choiceBlock.choices[choiceIndex];
      if (!choiceText) return;

      const isMcq = choiceBlock.kind === "mcq";
      const questionId = isMcq ? choiceBlock.questionId || null : null;

      onMessagesChange((prev) =>
        prev.map((m) => {
          if (m.id !== messageId) return m;
          const blocks = (m.blocks ?? []).map((b, i) => {
            if (i !== blockIndex || (b.kind !== "mcq" && b.kind !== "inline_response")) return b;
            if (b.submitted) return b;
            return { ...b, selectedChoiceIndex: choiceIndex, submitted: true };
          });
          return { ...m, blocks };
        })
      );

      void sendModalMessage(choiceText, isMcq ? { inlineResponseChoiceIndex: choiceIndex } : undefined);

      if (isMcq && questionId) {
        void setMcqQuestionBlockAnswerMutation({
          variables: {
            messageId,
            questionId,
            selectedChoiceIndex: choiceIndex,
          },
        });
      }
    },
    [isStreaming, messages, onMessagesChange, sendModalMessage, setMcqQuestionBlockAnswerMutation]
  );

  return (
    <VisualizationModal
      visible={visible}
      block={block}
      messages={messages}
      inputText={inputText}
      isStreaming={isStreaming}
      isMediaChatReady={isMediaChatReady}
      onInputChange={setInputText}
      onSend={handleSend}
      onClose={onClose}
      onChoiceSelect={handleChoiceSelect}
    />
  );
};

export default VisualizationModalContainer;
